import axios from 'axios';
import { isStringArray } from '@/api/queries/utils';
import { FilterType } from '@/models/filter/FilterType';
import { getHttpErrorStatus } from '@/utils/auth/isAuthError';
import { isLocalFiltersRecord } from '@/utils/filter/isLocalFiltersRecord';
import { isRecord } from '@/utils/filter/isRecord';
import { isAllowedDownloadUrl } from '@/utils/filter/unwrapS3Response';
import { isSharedViewFilterState } from '@/hooks/useApplySharedView.utils';
import { classifySharedViewFailure, type SharedViewFailureDiagnosis } from '@/utils/filter/classifySharedViewFailure';
import getEnvVar from '@/utils/getEnvVar';
import {
  describeType,
  readErrorRequestId,
  summarizePayload,
  summarizeResponse,
  type SharedViewPayloadSummary,
  type SharedViewResponseSummary,
} from '@/utils/filter/sharedViewResponseSummary';

export type SharedViewFailureStage =
  | 'fetchView'
  | 'invalidDownloadUrl'
  | 'downloadFilters'
  | 'invalidPayload'
  | 'validation';

export type SharedViewFailureReasonKind =
  | 'expectedObject'
  | 'expectedStringArray'
  | 'nonStringItems'
  | 'unknownKeys'
  | 'invalidObjectEntries'
  | 'invalidStringArrayEntries'
  | 'flatUnknownKeys'
  | 'emptyPayload'
  | 'downloadLinkMissing'
  | 'downloadLinkNotString'
  | 'downloadLinkMalformed'
  | 'downloadHostNotAllowed'
  | 'responseEnvelope'
  | 'responseStatusCode';

export type SharedViewFailureField =
  | 'globalFilters'
  | 'localFilters'
  | 'optionPaths'
  | 'payload'
  | 'response'
  | 'resolvedCountryCodes'
  | 'senderCountryCodes';

export interface SharedViewFailureReason {
  kind: SharedViewFailureReasonKind;
  field?: SharedViewFailureField;
  keys?: string[];
  actualType?: string;
  statusCode?: number;
}

export type SharedViewRequestInfo = 'filtersById' | 'preSignedS3';

export interface SharedViewFailureContext {
  stage: SharedViewFailureStage;
  shareViewId?: string;
  requestId?: string;
  rawPayload?: unknown;
  payload?: unknown;
  responseBody?: unknown;
}

export interface SharedViewFailureDetails {
  stage: SharedViewFailureStage;
  httpStatus?: number;
  errorCode?: string;
  errorName?: string;
  diagnosis: SharedViewFailureDiagnosis;
  occurredAt: string;
  appHost?: string;
  apiHost?: string;
  shareViewId?: string;
  requestId?: string;
  stagesCompleted: SharedViewFailureStage[];
  requestInfo?: SharedViewRequestInfo;
  response?: SharedViewResponseSummary;
  payloadSummary?: SharedViewPayloadSummary;
  reasons: SharedViewFailureReason[];
}

const FILTER_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(FilterType));

const getUnknownFilterKeys = (value: Record<string, unknown>): string[] =>
  Object.keys(value).filter(key => !FILTER_TYPE_VALUES.has(key));

const diagnoseGlobalFilters = (globalFilters: unknown): SharedViewFailureReason[] => {
  if (!isRecord(globalFilters)) {
    return [{ kind: 'expectedObject', field: 'globalFilters', actualType: describeType(globalFilters) }];
  }
  if (isSharedViewFilterState(globalFilters)) {
    return [];
  }
  return [{ kind: 'unknownKeys', field: 'globalFilters', keys: getUnknownFilterKeys(globalFilters) }];
};

const diagnoseLocalFilters = (localFilters: unknown): SharedViewFailureReason[] => {
  if (isLocalFiltersRecord(localFilters)) {
    return [];
  }
  if (!isRecord(localFilters)) {
    return [{ kind: 'expectedObject', field: 'localFilters', actualType: describeType(localFilters) }];
  }
  const invalidKeys = Object.keys(localFilters).filter(key => !isRecord(localFilters[key]));
  return [{ kind: 'invalidObjectEntries', field: 'localFilters', keys: invalidKeys }];
};

const diagnoseStringArray = (name: SharedViewFailureField, value: unknown): SharedViewFailureReason[] => {
  if (isStringArray(value)) {
    return [];
  }
  if (Array.isArray(value)) {
    return [{ kind: 'nonStringItems', field: name }];
  }
  return [{ kind: 'expectedStringArray', field: name, actualType: describeType(value) }];
};

const diagnoseOptionPaths = (optionPaths: unknown): SharedViewFailureReason[] => {
  if (!isRecord(optionPaths)) {
    return [{ kind: 'expectedObject', field: 'optionPaths', actualType: describeType(optionPaths) }];
  }
  const reasons: SharedViewFailureReason[] = [];
  const unknownKeys = getUnknownFilterKeys(optionPaths);
  if (unknownKeys.length > 0) {
    reasons.push({ kind: 'unknownKeys', field: 'optionPaths', keys: unknownKeys });
  }
  const invalidKeys = Object.keys(optionPaths).filter(
    key => FILTER_TYPE_VALUES.has(key) && !isStringArray(optionPaths[key]),
  );
  if (invalidKeys.length > 0) {
    reasons.push({ kind: 'invalidStringArrayEntries', field: 'optionPaths', keys: invalidKeys });
  }
  return reasons;
};

const diagnoseOptionalField = (
  data: Record<string, unknown>,
  name: SharedViewFailureField,
  diagnose: (value: unknown) => SharedViewFailureReason[],
) => (name in data && data[name] !== undefined ? diagnose(data[name]) : []);

const diagnoseFlatPayload = (data: Record<string, unknown>): SharedViewFailureReason[] => {
  if (Object.keys(data).length === 0) {
    return [{ kind: 'emptyPayload' }];
  }
  const unknownKeys = getUnknownFilterKeys(data);
  return unknownKeys.length > 0 ? [{ kind: 'flatUnknownKeys', keys: unknownKeys }] : [];
};

export const diagnoseSharedViewPayload = (data: unknown): SharedViewFailureReason[] => {
  if (!isRecord(data)) {
    return [{ kind: 'expectedObject', field: 'payload', actualType: describeType(data) }];
  }
  if (!('globalFilters' in data)) {
    return diagnoseFlatPayload(data);
  }
  return [
    ...diagnoseGlobalFilters(data.globalFilters),
    ...diagnoseOptionalField(data, 'localFilters', diagnoseLocalFilters),
    ...diagnoseOptionalField(data, 'resolvedCountryCodes', value => diagnoseStringArray('resolvedCountryCodes', value)),
    ...diagnoseOptionalField(data, 'senderCountryCodes', value => diagnoseStringArray('senderCountryCodes', value)),
    ...diagnoseOptionalField(data, 'optionPaths', diagnoseOptionPaths),
  ];
};

const ERROR_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;

const getErrorName = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('name' in error)) {
    return undefined;
  }
  const { name } = error;
  return typeof name === 'string' && ERROR_NAME_PATTERN.test(name) ? name : undefined;
};

const diagnoseDownloadLink = (responseBody: unknown): SharedViewFailureReason[] => {
  const link = isRecord(responseBody) ? responseBody.filters_download_link : undefined;
  if (link === undefined) {
    return [{ kind: 'downloadLinkMissing' }];
  }
  if (typeof link !== 'string') {
    return [{ kind: 'downloadLinkNotString', actualType: describeType(link) }];
  }
  try {
    return isAllowedDownloadUrl(new URL(link)) ? [] : [{ kind: 'downloadHostNotAllowed' }];
  } catch {
    return [{ kind: 'downloadLinkMalformed' }];
  }
};

const describeResponseEnvelope = (responseBody: unknown): SharedViewFailureReason[] => {
  if (!isRecord(responseBody)) {
    return [{ kind: 'expectedObject', field: 'response', actualType: describeType(responseBody) }];
  }
  const reasons: SharedViewFailureReason[] = [{ kind: 'responseEnvelope', keys: Object.keys(responseBody) }];
  const { statusCode } = responseBody;
  if (typeof statusCode === 'number') {
    reasons.push({ kind: 'responseStatusCode', statusCode });
  }
  return reasons;
};

const getStageReasons = ({
  stage,
  rawPayload,
  payload,
  responseBody,
}: SharedViewFailureContext): SharedViewFailureReason[] => {
  if (stage === 'invalidDownloadUrl') return diagnoseDownloadLink(responseBody);
  if (stage === 'invalidPayload') return diagnoseSharedViewPayload(payload ?? rawPayload);
  return [];
};

const STAGE_ORDER: SharedViewFailureStage[] = [
  'fetchView',
  'invalidDownloadUrl',
  'downloadFilters',
  'invalidPayload',
  'validation',
];

const REQUEST_INFO_BY_STAGE: Partial<Record<SharedViewFailureStage, SharedViewRequestInfo>> = {
  fetchView: 'filtersById',
  downloadFilters: 'preSignedS3',
};

const getApiHost = (): string | undefined => {
  try {
    const apiUrl = getEnvVar('VITE_DATA_API_URL');
    return apiUrl ? new URL(apiUrl).hostname : undefined;
  } catch {
    return undefined;
  }
};

export const buildSharedViewFailureDetails = (
  context: SharedViewFailureContext,
  error: unknown,
): SharedViewFailureDetails => {
  const details = {
    stage: context.stage,
    httpStatus: getHttpErrorStatus(error),
    errorCode: axios.isAxiosError(error) ? error.code : undefined,
    errorName: getErrorName(error),
    occurredAt: new Date().toISOString(),
    appHost: window.location.hostname || undefined,
    apiHost: getApiHost(),
    shareViewId: context.shareViewId,
    requestId: context.requestId ?? readErrorRequestId(error),
    stagesCompleted: STAGE_ORDER.slice(0, STAGE_ORDER.indexOf(context.stage)),
    requestInfo: REQUEST_INFO_BY_STAGE[context.stage],
    response: context.stage === 'fetchView' ? undefined : summarizeResponse(context.responseBody),
    payloadSummary: summarizePayload(context.rawPayload, context.payload),
    reasons: [
      ...getStageReasons(context),
      ...(context.stage !== 'fetchView' && context.responseBody !== undefined
        ? describeResponseEnvelope(context.responseBody)
        : []),
    ],
  };

  return { ...details, diagnosis: classifySharedViewFailure(details) };
};
